import { useState, type FormEvent } from 'react';
import { Check, Eye, EyeOff } from 'lucide-react';
import { api, ApiError } from '../api/client.ts';
import { useAuth } from '../auth/AuthContext.tsx';
import { MIN_PASSWORD_LENGTH, passwordChecks } from '../lib/password.ts';
import './PasswordForm.css';

/**
 * The fields, in one component, because the two screens that show them are one
 * form with one field's difference.
 *
 * From «Настройки» a password is changed on purpose, and the current one is the
 * proof that it is the owner asking. At the first sign-in it is replaced instead
 * of the one an admin handed out, and there is nothing to prove: that password
 * was typed at the login form a moment ago. Everything else — the rules, the
 * eye, what the button says when — is the same, so it is written once.
 */
export function PasswordForm({
  askCurrent,
  submitLabel,
}: {
  askCurrent: boolean;
  submitLabel: string;
}) {
  const { applyIdentity } = useAuth();

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [repeat, setRepeat] = useState('');
  const [busy, setBusy] = useState(false);
  /**
   * Only the server knows whether the current password is the current password,
   * so this arrives with the answer to a submit rather than while typing — and
   * it sits under the first field, where the mistake is, instead of at the
   * bottom with everything the form could work out for itself.
   */
  const [currentWrong, setCurrentWrong] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const checks = passwordChecks({ current, next, repeat, asks: askCurrent });

  /**
   * Everything the server said goes as soon as a field is touched. A refusal is
   * about the attempt that was made, and leaving it under a form somebody is
   * already fixing reads as a verdict on what they are typing now.
   */
  function edit(set: (value: string) => void) {
    return (value: string) => {
      set(value);
      setCurrentWrong(false);
      setError(null);
      setDone(false);
    };
  }

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (busy || !checks.canSubmit) return;
    setBusy(true);
    setCurrentWrong(false);
    setError(null);
    try {
      // The reply is the whole identity with the flag cleared, so applying it is
      // what takes the first-login screen away — there is nothing to navigate.
      applyIdentity(await api.changePassword(askCurrent ? current : null, next));
      setCurrent('');
      setNext('');
      setRepeat('');
      setDone(true);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setCurrentWrong(true);
      else setError(err instanceof ApiError ? err.message : 'Не удалось сменить пароль');
    }
    setBusy(false);
  }

  return (
    <form className="password-form" onSubmit={submit}>
      {askCurrent && (
        <Field
          id="password-current"
          label="Текущий пароль"
          placeholder="Введите текущий пароль"
          autoComplete="current-password"
          value={current}
          invalid={currentWrong}
          onChange={edit(setCurrent)}
          note={currentWrong ? { text: 'Неверный пароль', bad: true } : null}
        />
      )}

      <Field
        id="password-new"
        label="Новый пароль"
        placeholder={`Не короче ${MIN_PASSWORD_LENGTH} символов`}
        autoComplete="new-password"
        value={next}
        invalid={checks.tooShort}
        onChange={edit(setNext)}
        note={{ text: checks.hint, bad: checks.tooShort }}
      />

      <Field
        id="password-repeat"
        label={askCurrent ? 'Повторите новый пароль' : 'Повторите пароль'}
        placeholder="Ещё раз"
        autoComplete="new-password"
        value={repeat}
        invalid={checks.mismatch}
        onChange={edit(setRepeat)}
        note={checks.mismatch ? { text: 'Пароли не совпадают', bad: true } : null}
      />

      <button className="primary-button password-submit" type="submit" disabled={busy || !checks.canSubmit}>
        {submitLabel}
      </button>

      {error && <div className="form-error">{error}</div>}

      {done && (
        <div className="password-done">
          <Check size={16} strokeWidth={3} />
          Пароль изменён. Другие сеансы завершены.
        </div>
      )}
    </form>
  );
}

/**
 * One password field: the label, the box with the eye in it, and the line under
 * it. The eye is per field rather than one switch for the form, because the
 * reason to reveal one — «did I mistype the repeat» — is not a reason to put
 * the other two on screen.
 */
function Field({
  id,
  label,
  placeholder,
  autoComplete,
  value,
  invalid,
  onChange,
  note,
}: {
  id: string;
  label: string;
  placeholder: string;
  autoComplete: string;
  value: string;
  invalid: boolean;
  onChange: (value: string) => void;
  note: { text: string; bad: boolean } | null;
}) {
  const [shown, setShown] = useState(false);

  return (
    <div className="password-field">
      <label className="field-label" htmlFor={id}>
        {label}
      </label>
      <div className={`password-box${invalid ? ' password-box-bad' : ''}`}>
        <input
          id={id}
          className="password-input"
          type={shown ? 'text' : 'password'}
          value={value}
          placeholder={placeholder}
          autoComplete={autoComplete}
          autoCapitalize="none"
          onChange={(event) => onChange(event.target.value)}
        />
        <button
          type="button"
          className="icon-button password-eye"
          onClick={() => setShown((was) => !was)}
          aria-label={shown ? 'Скрыть пароль' : 'Показать пароль'}
          aria-pressed={shown}
        >
          {shown ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {note && <div className={`password-note${note.bad ? ' password-note-bad' : ''}`}>{note.text}</div>}
    </div>
  );
}
